import React, { useState, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import AsyncStorage from "@react-native-async-storage/async-storage";

export type CopyOption = "billing" | "payroll" | "both" | "none";

export interface ApproveTimecardModalProps {
  count: number;
  onConfirm: (copyOption: CopyOption, dontShowAgain: boolean) => void;
  onCancel: () => void;
}

export function ApproveTimecardModal({ count, onConfirm, onCancel }: ApproveTimecardModalProps) {
  const { colors } = useTheme();
  const [selectedOption, setSelectedOption] = useState<CopyOption>("none");
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Load default copy option on mount
  useEffect(() => {
    AsyncStorage.getItem("timecard_approve_default_copy").then((value) => {
      if (value) setSelectedOption(value as CopyOption);
    });
  }, []);

  const options: { value: CopyOption; label: string; icon: keyof typeof MaterialCommunityIcons.glyphMap }[] = [
    { value: "billing", label: "Copy to Billing", icon: "cash-register" },
    { value: "payroll", label: "Copy to Payroll", icon: "account-cash" },
    { value: "both", label: "Copy to Both", icon: "content-duplicate" },
    { value: "none", label: "Don't copy", icon: "close-circle-outline" },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {/* Header */}
      <View
        style={[styles.header, { backgroundColor: colors.complete.background, borderColor: colors.complete.border }]}
      >
        <MaterialCommunityIcons name="check-circle" size={24} color={colors.complete.text} />
        <ThemedText type="subtitle" style={{ color: colors.complete.text, marginLeft: 8 }}>
          Approve Timecards
        </ThemedText>
      </View>

      {/* Body */}
      <View style={styles.body}>
        {/* Message */}
        <View style={styles.messageRow}>
          <ThemedText style={styles.message}>
            You&apos;re about to set <ThemedText style={styles.count}>{count}</ThemedText> timecard
            {count !== 1 ? "s" : ""} to
          </ThemedText>
          <View style={[styles.statusBadge, { backgroundColor: colors.complete.border }]}>
            <ThemedText style={[styles.statusText, { color: "white" }]}>APPROVED</ThemedText>
          </View>
        </View>

        {/* Copy Options */}
        <ThemedText style={[styles.sectionLabel, { color: colors.textSecondary }]}>
          Would you like to copy these timecards?
        </ThemedText>

        <View style={styles.optionsContainer}>
          {options.map((option) => {
            const isSelected = selectedOption === option.value;
            return (
              <Pressable
                key={option.value}
                style={[
                  styles.optionItem,
                  {
                    backgroundColor: isSelected ? colors.complete.background : colors.backgroundSecondary,
                    borderColor: isSelected ? colors.complete.border : colors.border,
                  },
                ]}
                onPress={() => setSelectedOption(option.value)}
              >
                <MaterialCommunityIcons
                  name={isSelected ? "radiobox-marked" : "radiobox-blank"}
                  size={22}
                  color={isSelected ? colors.complete.text : colors.textSecondary}
                />
                <MaterialCommunityIcons
                  name={option.icon}
                  size={18}
                  color={isSelected ? colors.complete.text : colors.text}
                  style={{ marginLeft: 8 }}
                />
                <ThemedText style={[styles.optionLabel, { color: isSelected ? colors.complete.text : colors.text }]}>
                  {option.label}
                </ThemedText>
              </Pressable>
            );
          })}
        </View>

        {/* Don't show again checkbox */}
        <Pressable style={styles.checkboxRow} onPress={() => setDontShowAgain(!dontShowAgain)}>
          <MaterialCommunityIcons
            name={dontShowAgain ? "checkbox-marked" : "checkbox-blank-outline"}
            size={22}
            color={dontShowAgain ? colors.primary : colors.textSecondary}
          />
          <ThemedText style={[styles.checkboxLabel, { color: colors.textSecondary }]}>
            Don&apos;t show this message again
          </ThemedText>
        </Pressable>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Confirm"
          icon="check"
          onPress={() => onConfirm(selectedOption, dontShowAgain)}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          iconStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 420,
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  body: {
    padding: 16,
    gap: 16,
  },
  messageRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
  },
  count: {
    fontWeight: "700",
    fontSize: 16,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  sectionLabel: {
    fontSize: 13,
  },
  optionsContainer: {
    gap: 8,
  },
  optionItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkboxLabel: {
    fontSize: 13,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    padding: 12,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
